import * as amplitudeInstance from "@amplitude/analytics-browser";

import { IS_DEV_ENV, IS_TEST_ENV, isTestServer } from "../utilities/current_environment";

const AMPLITUDE_KEY = process.env.REACT_APP_AMPLITUDE_KEY;

// Email domains treated as internal users (Hexagon / Multivista staff) for analytics segmentation.
const INTERNAL_EMAIL_DOMAINS: ReadonlySet<string> = new Set<string>([
  "hexagon.com",
  "multivistasystems.com",
]);

export function getEmailDomain(email: string | null | undefined): string | null {
  if (!email || typeof email !== "string") {
    return null;
  }
  const parts = email.split("@");
  return parts.length === 2 && parts[1] ? parts[1].toLowerCase() : null;
}

export function getUserType(emailDomain: string | null): "internal" | "external" {
  return emailDomain != null && INTERNAL_EMAIL_DOMAINS.has(emailDomain) ? "internal" : "external";
}

let is_amplitude_initialized = false;

export function initiateAmplitudeTracking(user: string) {
  if (!is_amplitude_initialized) {
    // @ts-ignore
    amplitudeInstance.init(AMPLITUDE_KEY, {
      autocapture: {
        pageViews: false,
        sessions: false,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
      userId: user,
      trackingOptions: {
        trackClicks: false,
        trackForms: false,
        trackPageViews: false,
        trackScrolls: false
      }
    });

    const shouldOptOut = IS_TEST_ENV || IS_DEV_ENV || isTestServer();
    amplitudeInstance.setOptOut(shouldOptOut);

    const emailDomain = getEmailDomain(user);
    if (emailDomain) {
      const identifyEvent = new amplitudeInstance.Identify();
      identifyEvent.set("emailDomain", emailDomain);
      identifyEvent.set("userType", getUserType(emailDomain));
      amplitudeInstance.identify(identifyEvent);
    }

    is_amplitude_initialized = true;
  }
}

// Resets Amplitude identity (userId + user properties) so the next user re-initialises cleanly.
// Needed because signing out via redirect does not always trigger a full page reload, which would
// otherwise leave the previous user's userId / emailDomain / userType attached to the next session.
export function resetAmplitudeIdentity() {
  if (is_amplitude_initialized) {
    try {
      amplitudeInstance.reset();
    } catch (err) {
      console.error("failed to reset amplitude identity", err);
    }
    is_amplitude_initialized = false;
  }
}

export default amplitudeInstance;
